import {
  IconBell,
  IconChevronRight,
  IconEye,
  IconSearch,
} from '@tabler/icons-react'
import React, { useMemo, useState } from 'react'

import { AlertBadge, BetaLabel } from '@/components/customComponents'
import { ScalrEmptyBox } from '@/components/customComponents/ScalrEmptyBox'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  markAlertAsRead,
  selectAlertGroups,
  selectUnreadAlerts,
  useAppDispatch,
  useAppSelector,
} from '@/store'
import { Alert, AlertGroup } from '@/types/alerts'
import { cn } from '@/utils'

interface AlertCenterProps {
  className?: string
}

export const AlertCenter: React.FC<AlertCenterProps> = ({ className }) => {
  const dispatch = useAppDispatch()
  const alertGroups = useAppSelector(selectAlertGroups)
  const unreadAlerts = useAppSelector(selectUnreadAlerts)

  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const handleViewAlerts = (alerts: Alert[]) => {
    alerts.forEach((alert) => {
      if (!alert.isRead) {
        dispatch(markAlertAsRead(alert.id))
      }
    })
  }

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const filterAlerts = (alerts: Alert[], query: string): Alert[] => {
    if (!query.trim()) return alerts

    const searchWords = query.toLowerCase().trim().split(/\s+/)

    return alerts.filter((alert) => {
      const alertMessage = alert.message.toLowerCase()
      return searchWords.every((word) => alertMessage.includes(word))
    })
  }

  const getAllAlerts = (groups: AlertGroup[]): Alert[] => {
    const alerts: Alert[] = []
    const collectAlerts = (group: AlertGroup) => {
      alerts.push(...group.alerts)
      group.subGroups.forEach(collectAlerts)
    }
    groups.forEach(collectAlerts)
    return alerts
  }

  const groupAlertsByLowestTwoLevels = (alerts: Alert[]) => {
    const grouped = new Map<
      string,
      {
        alerts: Alert[]
        breadcrumb: string[]
        unreadCount: number
      }
    >()

    alerts.forEach((alert) => {
      const hierarchy = alert.hierarchy
      const path = [hierarchy.clarityLens]

      if (hierarchy.salesLens) path.push(hierarchy.salesLens)
      if (hierarchy.category) path.push(hierarchy.category)
      if (hierarchy.item) path.push(hierarchy.item)
      if (hierarchy.tab) path.push(hierarchy.tab)

      // Get the lowest two levels
      const lowestTwoLevels = path.slice(-2)
      const key = lowestTwoLevels.join(' > ')

      if (!grouped.has(key)) {
        grouped.set(key, {
          alerts: [],
          breadcrumb: path,
          unreadCount: 0,
        })
      }

      const group = grouped.get(key)!
      group.alerts.push(alert)
      if (!alert.isRead) {
        group.unreadCount++
      }
    })

    return Array.from(grouped.values())
  }

  const allAlerts = getAllAlerts(alertGroups)

  const filteredAndGroupedAlerts = useMemo(() => {
    const filteredAlerts = filterAlerts(allAlerts, searchQuery)
    return groupAlertsByLowestTwoLevels(filteredAlerts)
  }, [allAlerts, searchQuery])

  const renderBreadcrumb = (breadcrumb: string[]) => {
    return (
      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
        {breadcrumb.map((item, index) => (
          <React.Fragment key={index}>
            <span>{item}</span>
            {index < breadcrumb.length - 1 && (
              <IconChevronRight className="h-3 w-3" />
            )}
          </React.Fragment>
        ))}
      </div>
    )
  }

  return (
    <div className={cn('fixed bottom-6 right-6 z-50', className)}>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="relative shadow-lg hover:shadow-xl transition-shadow duration-200 bg-background border-2 h-12 w-12 rounded-xl"
          >
            <IconBell className="h-6 w-6" />
            {unreadAlerts > 0 && (
              <div className="absolute -top-1 -right-1 bg-red-500 text-gray-50 text-xs font-semibold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
                {unreadAlerts > 99 ? '99+' : unreadAlerts}
              </div>
            )}
            <span className="sr-only">Notifications</span>
          </Button>
        </DialogTrigger>

        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader className="border-b border-border pb-4">
            <div className="flex items-center justify-between">
              {/* TODO: change filter logic more similar to Ebrar's design */}
              <BetaLabel className="mt-[-10px] ml-[-20px]" />
              <div className="flex items-center gap-3 flex-1 max-w-[350px]">
                <div className="relative flex-1">
                  <IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search alerts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 rounded-lg"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 mr-10">
                {/* <Button
                  variant="outline"
                  size="sm"
                  onClick={clearAllAlerts}
                  className="text-xs rounded-lg ml-4"
                >
                  Clear All
                </Button> */}
                {unreadAlerts > 0 && <AlertBadge unreadAlerts={unreadAlerts} />}
              </div>
            </div>
          </DialogHeader>

          <div>
            {filteredAndGroupedAlerts.length === 0 ? (
              <ScalrEmptyBox
                title={searchQuery.trim() ? 'No Matching Alerts' : 'No Alerts'}
                message={
                  searchQuery.trim()
                    ? `No alerts found matching "${searchQuery}". Try adjusting your search terms.`
                    : 'There are currently no alerts to display. Check back later for updates.'
                }
                icon={({ size, className }) => (
                  <IconBell size={size} className={className} />
                )}
                size="md"
                className="py-8"
              />
            ) : (
              <div className="h-[70vh] space-y-4 overflow-y-auto px-6 py-4">
                {filteredAndGroupedAlerts.map((group, index) => (
                  <div
                    key={index}
                    className="border border-border rounded-xl bg-card p-4"
                  >
                    {/* Breadcrumb */}
                    {renderBreadcrumb(group.breadcrumb)}

                    {/* Group Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium text-foreground leading-none tracking-tight">
                          {group.breadcrumb[group.breadcrumb.length - 1]}
                        </h3>
                        {group.unreadCount > 0 && (
                          <AlertBadge unreadAlerts={group.unreadCount} />
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewAlerts(group.alerts)}
                        className="flex items-center gap-1 rounded-lg"
                      >
                        View <IconEye className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Alerts List */}
                    <div className="space-y-3">
                      {group.alerts.map((alert) => (
                        <div
                          key={alert.id}
                          className="border-l-2 border-muted pl-4 py-2"
                        >
                          <div className="text-sm text-muted-foreground mb-1">
                            {formatDate(alert.timestamp)}
                          </div>
                          <div className="text-sm text-foreground leading-relaxed">
                            {alert.message}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
